<template>
  <section
    class="relative py-20 lg:py-32 overflow-hidden"
    aria-label="Hero"
  >
    <div class="absolute inset-0 z-0">
      <div v-if="bannerUrl" class="absolute inset-0">
        <img
          :src="bannerUrl"
          :alt="name ? `${name} — business` : 'Business'"
          class="w-full h-full object-cover opacity-30 dark:opacity-20"
          loading="eager"
        >
        <div class="absolute inset-0 bg-[#f8f6f6]/80 dark:bg-[#221610]/80" aria-hidden="true" />
      </div>
      <div v-else class="absolute inset-0 bg-gradient-to-b from-slate-100 to-[#f8f6f6] dark:from-slate-900 dark:to-[#221610]" aria-hidden="true" />
    </div>
    <div class="max-w-7xl mx-auto px-6 relative z-10 flex flex-col items-center text-center">
      <button
        v-if="logoUrl && canOpenOwnerLogoModal"
        type="button"
        class="w-24 h-24 mb-8 rounded-2xl flex items-center justify-center overflow-hidden shrink-0 border-2 border-white/20 dark:border-slate-600 shadow-lg bg-white dark:bg-slate-800 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        :style="{ '--tw-ring-color': 'var(--rms-t4-primary,#ec5b13)' }"
        :aria-label="$t('public.ownerEditLogoAria')"
        data-testid="public-hero-logo"
        @click="openOwnerLogoModal"
      >
        <img :src="logoUrl" :alt="name || 'Business'" class="w-full h-full object-contain">
      </button>
      <div
        v-else-if="logoUrl"
        class="w-24 h-24 mb-8 rounded-2xl flex items-center justify-center overflow-hidden shrink-0 border-2 border-white/20 dark:border-slate-600 shadow-lg bg-white dark:bg-slate-800"
        data-testid="public-hero-logo"
      >
        <img :src="logoUrl" :alt="name || 'Business'" class="w-full h-full object-contain">
      </div>
      <button
        v-else-if="viewer?.is_owner && ownerViewMode && canOpenOwnerLogoModal"
        type="button"
        class="mb-8 max-w-[200px] mx-auto block rounded border-0 bg-transparent p-0 text-left cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        :style="{ '--tw-ring-color': 'var(--rms-t4-primary,#ec5b13)' }"
        :aria-label="$t('public.ownerEditLogoAria')"
        @click="openOwnerLogoModal"
      >
        <div class="py-4 px-4 rounded border border-dashed border-slate-300/60 dark:border-slate-600/60 bg-slate-200/50 dark:bg-slate-700/20">
          <p class="text-sm font-mono text-slate-600 dark:text-slate-400">{{ $t('public.noLogoSetOwnerMessage') }}</p>
        </div>
      </button>
      <div v-else-if="viewer?.is_owner && ownerViewMode" class="mb-8 max-w-[200px] mx-auto py-4 px-4 rounded border border-dashed border-slate-300/60 dark:border-slate-600/60 bg-slate-200/50 dark:bg-slate-700/20">
        <p class="text-sm font-mono text-slate-600 dark:text-slate-400">{{ $t('public.noLogoSetOwnerMessage') }}</p>
      </div>
      <div v-else class="w-24 h-24 mb-8 rounded-2xl flex items-center justify-center shrink-0" :style="{ backgroundColor: primaryColor ? `${primaryColor}20` : 'rgb(236 91 19 / 0.1)' }">
        <span class="material-symbols-outlined text-5xl" :style="{ color: primaryColor || '#ec5b13' }">storefront</span>
      </div>
      <div class="flex justify-center w-full mb-6">
        <PublicOpenStatusBadge :operating-hours="operatingHours" variant="t4-hero" />
      </div>
      <h1 class="text-5xl lg:text-7xl font-black tracking-tight mb-6 text-slate-900 dark:text-slate-100">
        {{ heroTitle }}
      </h1>
      <p v-if="heroSubtext" class="max-w-2xl text-lg lg:text-xl text-slate-600 dark:text-slate-400 mb-10 leading-relaxed">
        {{ heroSubtext }}
      </p>
      <div v-else-if="viewer?.is_owner && ownerViewMode" class="max-w-md mx-auto mb-10 py-4 px-4 rounded border border-dashed border-slate-300/60 dark:border-slate-600/60 bg-slate-200/50 dark:bg-slate-700/20">
        <p class="text-sm font-mono text-slate-600 dark:text-slate-400">{{ $t('public.noTaglineOrDescriptionSetOwnerMessage') }}</p>
      </div>
      <div class="flex flex-col sm:flex-row gap-4 items-center flex-wrap">
        <a
          href="#menu"
          class="min-h-[44px] px-8 py-4 rounded-xl font-bold text-lg text-white hover:opacity-90 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 inline-flex items-center justify-center"
          :style="{ backgroundColor: primaryColor || '#ec5b13' }"
        >
          {{ viewListLabel || $t('public.viewMenu') }}
        </a>
        <a
          v-if="hasAbout"
          href="#about"
          class="min-h-[44px] px-8 py-4 rounded-xl font-bold text-lg bg-slate-200 dark:bg-slate-800 text-slate-900 dark:text-white hover:bg-slate-300 dark:hover:bg-slate-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 inline-flex items-center justify-center"
        >
          {{ $t('public.t3NavAbout') }}
        </a>
        <div
          v-else-if="viewer?.is_owner && ownerViewMode"
          class="py-3 px-4 rounded-xl border border-dashed border-slate-300/60 dark:border-slate-600/60 bg-slate-200/50 dark:bg-slate-700/20 max-w-[200px]"
        >
          <p class="text-xs font-mono text-slate-600 dark:text-slate-400">{{ $t('public.ownerNavAboutUnavailable') }}</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue'
import PublicOpenStatusBadge from '@/components/public/PublicOpenStatusBadge.vue'
import { usePublicOwnerLogoModal } from '@/composables/usePublicOwnerLogoModal'

const { canOpen: canOpenOwnerLogoModal, openOwnerLogoModal } = usePublicOwnerLogoModal()

const props = defineProps({
  name: { type: String, default: '' },
  tagline: { type: String, default: '' },
  description: { type: String, default: '' },
  bannerUrl: { type: String, default: '' },
  logoUrl: { type: String, default: '' },
  operatingHours: { type: Object, default: null },
  primaryColor: { type: String, default: null },
  viewListLabel: { type: String, default: '' },
  hasAbout: { type: Boolean, default: false },
  viewer: { type: Object, default: () => ({ is_owner: false }) },
  ownerViewMode: { type: Boolean, default: true },
})

const heroTitle = computed(() => (props.name || 'Our Business').trim() || 'Our Business')
const heroSubtext = computed(() => (props.tagline || props.description || '').trim())
</script>
